import { coreMessages, type AppLocale } from './base-messages';

export const en: AppLocale = {
  ...coreMessages,
  dataTable: {
    rowModeLabel: 'Row display',
    rowModeEllipsis: 'Truncate (…)',
    rowModeScroll: 'Single line + horizontal scroll',
    rowModeWrap: 'Wrap lines',
    columnsLabel: 'Columns',
    reorderColumnHint: 'Drag the handle to move this column',
    resetThisTablePrefs: 'Reset this table',
    resetThisTablePrefsTitle: 'Clear saved layout for this table only (row mode, columns, order, widths). Does not change your global user settings.',
    dataOperationsLabel: 'Data operations',
    operationExportExcel: 'Export Excel',
    operationDownloadTemplate: 'Download template',
    operationUploadTemplate: 'Upload template',
    operationImportHistory: 'Import history',
    operationFieldDictionary: 'Field dictionary',
    operationUnsupported: 'This table does not support this operation.',
    operationNoPermission: 'You do not have permission for this operation.',
    operationNotReady: 'This operation is not ready yet.',
    customFieldColumnsSectionLabel: 'Custom fields',
    importModalTitle: 'Upload template',
    importChooseFile: 'Choose CSV file',
    importChooseFileHint: 'Fill and upload the template downloaded from this table.',
    importNoFile: 'No file selected.',
    importSummary: 'Import summary',
    importReadyRows: 'Ready rows',
    importErrorRows: 'Error rows',
    importWarnings: 'Warnings',
    importUnknownHeaders: 'Unknown headers',
    importMissingRequiredHeaders: 'Missing required headers',
    importPreview: 'Preview',
    importSubmit: 'Start import',
    importCancel: 'Cancel',
    importParsing: 'Reading file...',
    importSubmitting: 'Importing...',
    importNoValidRows: 'There are no valid rows to import.',
    importFailed: 'Import failed.',
    importSucceeded: 'Import completed.',
    operationExportCsv: 'Export CSV',
    operationExportPdf: 'Export PDF',
    operationNoRows: 'There are no visible rows to export.',
    importUnknownHeader: 'Unknown column: {header}',
    importMissingRequiredHeader: 'Missing required column: {header}',
    importRequiredCell: '{header} is required.',
    importMaxRowsExceeded: 'The file has more than {maxRows} rows; only the first {maxRows} rows were evaluated.',
    refreshLabel: 'Refresh',
    focusModeEnterLabel: 'Focus table',
    focusModeFullscreenLabel: 'Enter full screen',
    focusModeExitLabel: 'Exit table focus',
    horizontalScrollLabel: 'Scroll table columns',
    selectionModeLabel: 'Select rows',
    selectAllRowsLabel: 'Select all rows',
    selectRowLabel: 'Select row',
    bulkSelectedLabel: '{count} selected',
    bulkActionsLabel: 'Bulk actions',
    tableSettingsLabel: 'Table settings',
    appearanceSectionLabel: 'Appearance',
    columnsSectionLabel: 'Columns',
    rowActionsLabel: 'Row actions',
  },
  passwordReset: {
    title: 'Set a New Password',
    description: 'Choose a strong new password that is known only to you.',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordHelp: 'Use at least 12 characters and at least three of these types: lowercase, uppercase, number, special character.',
    mismatch: 'Passwords do not match.',
    invalidLink: 'The password reset link is missing, invalid, or expired.',
    submit: 'Update password',
    submitting: 'Updating password...',
    success: 'Your password has been updated. You can now sign in with the new password.',
    backToLogin: 'Back to sign in',
    requestTitle: 'Send password reset link',
    requestDescription: 'A one-time link valid for 30 minutes will be sent to the user’s registered email address. The current password remains unchanged until the link is used.',
    requestSend: 'Send link',
    requestSending: 'Sending...',
    requestSuccess: 'The password reset link was accepted for delivery.',
    requestError: 'The password reset link could not be sent.',
    selfServiceOpen: 'Forgot password',
    selfServiceTitle: 'Forgot Password',
    selfServiceDescription: 'Enter your corporate account email. If it matches an account, a one-time link valid for 30 minutes will be sent.',
    selfServiceEmailLabel: 'Email',
    selfServiceSubmit: 'Request link',
    selfServiceSubmitting: 'Sending request...',
    selfServiceAcknowledgement: 'If this email is registered, a password reset link will be sent. Please check your inbox and spam folder.',
    selfServiceClose: 'Close',
    selfServiceCancel: 'Cancel',
    selfServiceError: 'The password reset request could not be completed. Please try again later.',
    selfServiceRateLimited: 'Too many requests. Please try again later.',
  },
  relationshipGraph: {
    sourceColumn: 'Source',
    relationshipColumn: 'Relationship',
    targetColumn: 'Related record',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    controlsLabel: 'Map controls',
    title: 'Relationship Map',
    diagramLabel: 'Record relationships',
    diagramHelp: 'Select a card to inspect recorded connections.',
    detailsTitle: 'Record details',
    connectionsTitle: 'Connections',
    noConnections: 'No connections',
    loading: 'Loading relationships…',
    forbidden: 'You do not have access to these relationships.',
    error: 'Relationships could not be loaded. Close and try again.',
    empty: 'This record has no displayable connections.',
    invalid: 'Relationship data could not be verified. Close and try again.',
    close: 'Close relationship map',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fitView: 'Fit map',
  },
  permissionPicker: {
    application: 'Application',
    module: 'Module',
    action: 'Action',
    permissions: 'Permissions',
    description: 'Description',
    selection: 'Selection',
    sharedPermissions: 'Shared permissions',
    selectedPermissions: '{count} permissions selected',
    selectedOnly: 'Selected only',
    unselectedOnly: 'Unselected only',
    selectedFirst: 'Selected first',
    noPermissions: 'No permissions',
  },
  errors: {
    ...coreMessages.errors,
    unknown: 'An unknown error occurred',
    network: 'Could not reach the server. Check your connection.',
    timeout: 'The request timed out. Please try again.',
    badRequest: 'Invalid request.',
    unauthorized: 'You need to sign in.',
    forbidden: 'You do not have permission for this action.',
    notFound: 'The requested resource was not found.',
    tooManyRequests: 'Too many requests. Please wait and try again.',
    internalServerError: 'A server error occurred. Please try again later.',
    badGateway: 'Bad gateway. Please try again later.',
    serviceUnavailable: 'Service is temporarily unavailable.',
    gatewayTimeout: 'Gateway timeout. Please try again.',
    serverErrorWithStatus: 'Server error ({status})',
  },
  common: {
    databaseUnavailableDeveloperHint: 'Developer note: In local development this usually means VPN is off or your IP is not allowed. Please check VPN and database access.',
    ...coreMessages.common,
    requestTimeout: 'The request timed out. Please try again.',
    backendConnectionErrorDev: 'Cannot connect to the API. Check VITE_API_BASE_URL / proxy and that the API is running.',
    backendConnectionErrorProd: 'Cannot connect to the server. Please try again later.',
    systemUpdating: 'The system is being updated. Please try again in a moment.',
    serverConfiguring: 'The server is being configured. Please wait…',
    forbidden: 'Access denied.',
    network: 'Network error.',
    select: 'Select…',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    edit: 'Edit',
    view: 'View',
    copy: 'Copy',
    delete: 'Delete',
    appLauncher: 'Applications',
    allApps: 'All Applications',
    loading: 'Loading...',
    noData: 'No data',
    code: 'Code',
    profileMenu: 'Profile menu',
    selectLanguage: 'Select language',
    perPage: 'Per page',
    firstPage: 'First page',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    lastPage: 'Last page',
    pleaseWait: 'Please wait',
    migrationDescription: 'Database migration may be in progress.',
    changeText: 'Change',
    deleteLogo: 'Remove logo',
    logoUploadHint: 'PNG or JPG, max a few MB.',
    deleteAvatar: 'Remove photo',
    avatarUploadHint: 'PNG or JPG.',
    newRecord: 'New record',
    noPermission: 'You do not have permission',
    searchPlaceholder: 'Search…',
    allStatuses: 'All statuses',
    active: 'Active',
    inactive: 'Inactive',
    clearFilters: 'Clear filters',
    all: 'All',
    clickToChangeStatus: 'Click to change status',
    noChangePermission: 'No permission to change status',
    statusDistribution: 'Status distribution',
    hide: 'Hide',
    show: 'Show',
    companies: 'Companies',
    adminConsole: 'Admin Console',
    manageAccount: 'Manage account',
    manageYourAccount: 'Manage your account',
    notificationsTitle: 'Notifications',
    notificationsUnread: 'unread',
    notificationsTypesMuted: 'types muted',
    notificationsViewAll: 'View All',
    notificationsPreferences: 'Preferences',
    notificationsMutedLabel: 'Muted',
    notificationsEmpty: 'No notifications',
    selectAll: 'Select all',
    id: 'ID',
    remove: 'Remove',
    syncDeletedResult: '{count} orphaned entries removed.',
    distributionByTable: 'Distribution by table (context)',
    distributionByLocale: 'Distribution by language (locale)',
    translationReport: 'Translation Status Distribution',
    page: 'Page',
    rows: 'rows',
    actions: 'Actions',
    create: 'Create',
    search: 'Search',
    closeSearch: 'Close search',
    help: 'Help',
    helpCenter: 'Help Center',
    helpAboutPage: 'About this page',
    helpDocumentation: 'Documentation',
    helpKeyboardShortcuts: 'Keyboard shortcuts',
    helpWhatsNew: 'What’s new',
    helpF1Hint: 'Press F1 for help about this page',
    helpNoDocsForPage: 'Documentation is not available for this page yet',
    aiAssistantTitle: 'QubePro AI Assistant',
    aiAssistantButton: 'AI Assistant',
    aiAssistantUnavailable: 'AI assistant is currently unavailable.',
    appearancePreference: 'Appearance Preference',
    modalControlPlacement: 'Form Control Placement',
    modalControlPlacementHint: 'Sets the position of close and fullscreen controls in forms.',
    modalControlPlacementSystem: 'System (Recommended)',
    modalControlPlacementWindows: 'Windows',
    modalControlPlacementMacos: 'macOS',
    productTour: {
      start: 'Start product tour',
      restart: 'Restart product tour',
      navigationTitle: 'Sidebar and modules',
      navigationDescription: 'Use the sidebar to reach authorized modules and pages, and expand or collapse each section.',
      appsTitle: 'Applications',
      appsDescription: 'Open your QubePro applications and switch between them here.',
      profileTitle: 'Profile and company',
      profileDescription: 'Manage your profile, selected company, and language preferences here.',
      aiTitle: 'QubePro AI',
      aiDescription: 'Open the QubePro AI assistant for help related to your current page.',
      helpTitle: 'Help Center',
      helpDescription: 'Open the documentation or restart this product tour at any time.',
      next: 'Next',
      previous: 'Previous',
      done: 'Done',
      close: 'Close',
      neverShowAgain: 'Do not show automatically again',
      progress: '{{current}} / {{total}}',
    },
    userExtensions: 'Extensions',
    close: 'Close',
    modalFocus: 'Focus form',
    modalFullscreen: 'Enter full screen',
    modalRestore: 'Restore size',
    filterByColumn: 'Filter by {column}',
    formCustomFieldsLoading: 'Custom fields are loading…',
    formCustomFieldsLoadError: 'Custom fields could not be loaded. Retry before saving to prevent data loss.',
    formCustomFieldsRetry: 'Retry',
    pageUnavailableTitle: 'The page could not be opened',
    pageUnavailableMessage: 'You can open another page from the menu or refresh this page.',
    pageReloadWarning: 'Refreshing discards unsaved changes.',
    allTargetLanguages: 'All languages (10 languages)',
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    errorOccurred: 'An error occurred',
    confirm: 'Confirm',
    context: 'Table',
    key: 'Column',
    originalShort: 'Orig.',
    originalValue: 'Original Value',
    locale: 'Locale',
    value: 'Value',
    status: 'Status',
    auto: 'Auto',
    manual: 'Manual',
    manageLocales: 'Manage Languages',
    updateUntranslatedFields: 'Sync New Fields',
    autoTranslate: 'Auto Translate',
    sourceAndTargetMustDiffer: 'Source and target language must differ.',
    autoTranslateSuccessCount: '{count} items auto-translated.',
    syncEntriesResult: 'Synced {count} new translation entries.',
    translated: 'Translated',
    missing: 'Missing',
    autoTranslateScope: 'Auto-Translate Scope',
    updateUntranslatedFieldsHint: 'Click "Update Untranslated Fields" to sync content from these tables.',
    processing: 'Processing...',
    enterTargetLanguage: 'Select target language. Translation will be from the source language below.',
    sourceLanguage: 'Source language',
    selectLanguagesFirst: 'Select languages in Manage Languages first',
    sourceLanguageLabel: 'Source language (your UI language)',
    targetLanguage: 'Target language',
    activeLocalesDescription: 'Select which languages are active system-wide. Saved as settings only; no new translations are added.',
    selectSourceLanguageForSync: 'Which language are the existing contents in? Original records will be marked in this language.',
    languageNames: {
      en: 'English',
      tr: 'Turkish',
      de: 'German',
      fr: 'French',
      es: 'Spanish',
      ar: 'Arabic',
      it: 'Italian',
      ja: 'Japanese',
      zh: 'Chinese',
      ru: 'Russian',
    },
    warning: 'Warning',
  },
  login: {
    ...coreMessages.login,
    loginFailed: 'Sign-in failed',
    emailRequired: 'Email is required',
    passwordRequired: 'Password is required',
    backendUnavailable: 'Authentication service is unavailable.',
    backendConnectFailed: 'Could not connect to the authentication service.',
    backendConnectFailedDev: 'Could not reach the API (development). Check the server and VITE_API_BASE_URL.',
    emailLabel: 'Email',
    emailPlaceholderFull: 'Enter your email address',
    passwordLabel: 'Password',
    passwordPlaceholderFull: 'Your password',
    hidePassword: 'Hide password',
    showPassword: 'Show password',
    loggingIn: 'Signing in…',
    submitButton: 'Sign in',
    errors: {
      ...coreMessages.login.errors,
      invalidCredentials: 'Invalid email or password.',
      sessionTooLarge: 'Session data is too large. Try again or clear site data.',
      databaseUnavailable: 'Database is unavailable.',
      accountInactive: 'Account is inactive.',
      sessionExpired: 'Session expired.',
      tooManyAttempts: 'Too many attempts. Try again later.',
    },
    featureTitle: "Connect your operations and financial records",
    featureDescription: "Track inventory, purchasing, sales and financial records with QubePro ERP.",
    featureCard1Title: "Products and inventory",
    featureCard1Description: "Manage products, warehouses and inventory movements.",
    featureCard2Title: "Purchasing",
    featureCard2Description: "Track purchasing documents and orders.",
    featureCard3Title: "Sales documents",
    featureCard3Description: "Manage sales orders and invoices.",
    featureCard4Title: "Financial movements",
    featureCard4Description: "Review bank, cash and financial records.",
    darkMode: 'Dark mode',
    lightMode: 'Light mode',
    pleaseWait: 'Please wait',
    serverConfiguring: 'The server is being configured…',
    dbCreating: 'Preparing your workspace…',
    loginPageTitle: 'Sign in',
    languageToggleLabel: 'Language',
  },
  sidebar: {
    ...coreMessages.sidebar,
    dashboard: 'Dashboard',
    settings: 'Settings',
    toggleMenu: 'Open menu',
    toggleMenuClose: 'Close menu',
    companies: 'Companies',
    logout: 'Log out',
    activityLogs: 'Activity logs',
  },
  customFields: {
    title: 'Custom Fields',
    newField: 'New Custom Field',
    editField: 'Edit Custom Field',
    entityType: 'Entity Type',
    code: 'Field Code',
    label: 'Display Name',
    codePlaceholder: 'e.g. riskLevel',
    labelPlaceholder: 'e.g. Special Code 1',
    dataType: 'Data Type',
    type: 'Type',
    scope: 'Scope',
    companyId: 'Company ID',
    companySelection: 'Companies',
    companySelectionPlaceholder: 'Blank is global, or select companies',
    selectedCompanies: '{{count}} companies selected',
    selectAllCompanies: 'Select all companies',
    sortOrder: 'Sort Order',
    required: 'Required',
    searchable: 'Searchable',
    showInForm: 'Show in form',
    showInList: 'Show column',
    formTab: 'Form Tab',
    formTabSelect: 'Form Tab',
    formTabPlaceholder: 'Blank uses the Custom Fields tab',
    defaultCustomTab: 'Custom Fields',
    selectEntityTypeFirst: 'Select entity type first',
    newFormTab: 'New tab',
    tabCode: 'Tab Code',
    tabName: 'Tab Name',
    tabCodePlaceholder: 'e.g. operations',
    tabNamePlaceholder: 'e.g. Operations',
    fieldWidth: 'Row Layout',
    widthFull: '1 field per row',
    widthHalf: '2 fields per row',
    widthThird: '3 fields per row',
    widthQuarter: '4 fields per row',
    systemField: 'System Field',
    description: 'Description',
    options: 'Options',
    optionsPlaceholder: 'Each line: code:Label\nExample: draft:Draft\nor just Draft',
    defaultValue: 'Default Value',
    statusDistributionTitle: '{{title}} Status Distribution',
    emptyMessage: 'No custom fields defined yet.',
    globalScope: 'Global',
    companyScope: 'Company {{id}}',
    deleteSelected: 'Delete selected',
    deleteSelectedMessage: '{{count}} custom fields will be deleted. This action cannot be undone.',
    formDesigner: 'Form Design',
    formDesignerTitle: 'Custom Field Form Design',
    formDesignerEntityType: 'Record type to design',
    formDesignerAllTabs: 'All',
    formDesignerEmpty: 'There are no custom fields for this record type.',
    formDesignerOriginalNotice: 'Original form fields are preserved; this screen manages custom field tabs, order, and row layout.',
    formDesignerSave: 'Save Design',
    formDesignerSaved: 'Form design saved.',
    formDesignerMoveUp: 'Move up',
    formDesignerMoveDown: 'Move down',
    formDesignerFieldSettings: 'Field layout',
    formDesignerQuickSave: 'Save',
    formDesignerEditTab: 'Edit tab',
    formDesignerDeleteTab: 'Delete tab',
    formDesignerTabEditorTitle: 'Tab settings',
    formDesignerCloseTabEditor: 'Close tab editor',
    dataTypes: {
      text: 'Text',
      number: 'Number',
      date: 'Date',
      datetime: 'Date/Time',
      boolean: 'Yes/No',
      select: 'Select',
      json: 'JSON',
    },
  },
  activityLogs: {
    title: 'Activity Logs',
    searchPlaceholder: 'Search (entity name, module, action)…',
    allModules: 'All modules',
    allActions: 'All actions',
    allSeverity: 'All severities',
    allApps: 'All applications',
    startDate: 'Start date',
    endDate: 'End date',
    exportCsv: 'Export CSV',
    total: 'Total',
    today: 'Today',
    critical: 'Critical',
    warning: 'Warning',
    severityInfo: 'Info',
    exportFilenameStem: 'activity-logs',
    noLogs: 'No activity logs found.',   system: 'System',
    field: 'Field',
    oldValue: 'Old value',
    newValue: 'New value',
    ip: 'IP',
    meta: 'Request metadata',
    userAgent: 'User agent',
    entity: 'Entity',
    severityCol: 'Severity',
    userCol: 'User',
    actionCol: 'Action',
    moduleCol: 'Module',
    dateCol: 'Date',
    appCol: 'Application',
    overviewTitle: 'Overview',
    severityChart: 'Severity distribution',
    topModulesChart: 'Top modules by volume',
    csvId: 'ID',
    csvDate: 'Date',
    csvUser: 'User',
    csvAction: 'Action',
    csvModule: 'Module',
    csvEntity: 'Entity',
    csvSeverity: 'Severity',
    csvIp: 'IP',
    csvApplication: 'Application',
    companyCol: 'Company ID',
    allOutcomes: 'All Outcomes',
    noLogsDesc: 'Logs will appear here when users perform actions.',
    entityType: 'Entity type',
    actorType: 'Actor type',
    outcome: 'Outcome',
    successful: 'Successful',
    failed: 'Failed',
    denied: 'Denied',
    reason: 'Reason',
    requestId: 'Request ID',
    correlationId: 'Correlation ID',
    integrity: 'Integrity proof',
    outboxWarning: 'Audit delivery queue requires attention.',
  },
};
